import CustomImage from '@/components/CustomImage/CustomImage.jsx';
import { getProductInfo, selectItem } from '@/utils/analytics.js';
import Link from 'next/link';
import React, { useRef, useEffect, useState, useCallback, useContext } from 'react';
import { ProductCatalogueV2, SnackBar, OffersSnackbar } from '@desquared/wave-components-library';

import { BundleOfferSnackBarOpen } from '@/store/bundleOfferSnackBar';
import { groupProductsByCategory } from '@/utils/category';
import {
  addProduct,
  addQuantityLimit,
  removeProduct,
  getBundleOfferDescription,
  getProductPropsWithCleanUrl,
  getProductBasedOnSlug,
  getProductIndexBasedOnSlug,
} from '@/utils/products';
import { openListSideSheet } from '@/utils/products';
import { checkListStatus } from '@/utils/lists';
import { BundleOfferSideSheetStore } from '@/store/sideSheet';
import { SelectedBundleOfferStore } from '@/store/selectedBundleOfferId';

import styles from '@/styles/Categories.module.css';
import { useTranslations } from 'next-intl';
import { CurrencyContext } from '@/contexts/CurrencyProvider';

export default function OffersCatalogue({
  products,
  handleLoadMore,
  isFetchingNextPage,
  onEmptyClick,
  isLoading,
  limitations,
  side_banner,
  bundleOffersSummary,
  defaultProductImage,
  selectedCategory,
}) {
  const observer = useRef(null);
  const [showSnackBar, setShowSnackbar] = useState(false);
  const [showBundleOffersSnackBar, setShowBundleOffersSnackBar] = useState(false);
  const { formatter } = useContext(CurrencyContext);
  const t = useTranslations();

  const offersCatalogueWordings = {
    noItemsFoundWording: t('offers.empty.no_items_found'),
    noItemsFoundDescription: t('offers.empty.no_items_found_description'),
    addItemWording: t('offers.empty.add_wording'),
    hashTags: t('offers.empty.hash_tags'),
    maxNumber: t('offers.empty.max_number'),
    listsSaved: t('offers.empty.lists_saved'),
  };

  const lastResultItemRef = useCallback(
    node => {
      if (!node) return;
      if (isLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      });

      observer.current.observe(node);
    },
    [isLoading, isFetchingNextPage, handleLoadMore]
  );

  useEffect(() => {
    const BundleOfferSnackBarOpen$ = BundleOfferSnackBarOpen.subscribe(setShowBundleOffersSnackBar);

    return () => {
      BundleOfferSnackBarOpen$.unsubscribe();
    };
  }, []);
  const groupedProducts = groupProductsByCategory(products, null);

  addQuantityLimit(groupedProducts, setShowSnackbar, limitations);

  return (
    <>
      {isLoading ? (
        <ProductCatalogueV2 products={new Array(1)} isLoading />
      ) : groupedProducts.length === 0 ? (
        <ProductCatalogueV2
          title={''}
          products={[]}
          onListClick={openListSideSheet}
          checkStatus={checkListStatus}
          addProduct={product =>
            addProduct(
              product,
              false,
              {
                item_list_id: 'offersCatalogue',
                item_list_name: 'offersCatalogue',
              },
              false,
              t('common.asides.side_cart.no_comment')
            )
          }
          removeProduct={product =>
            removeProduct(
              product,
              {
                item_list_id: 'offersCatalogue',
                item_list_name: 'offersCatalogue',
              },
              t('common.asides.side_cart.no_comment')
            )
          }
          image={side_banner}
          noItemsFoundWording={offersCatalogueWordings.noItemsFoundWording}
          noItemsFoundDescription={offersCatalogueWordings.noItemsFoundDescription}
          addItemWording={offersCatalogueWordings.addItemWording}
          hashTags={offersCatalogueWordings.hashTags}
          onAdd={onEmptyClick}
          defaultQsText={t('common.qs.default')}
          defaultQsUnavailableText={t('common.qs.default_unavailable')}
          ImageComponent={CustomImage}
          offerLabelHasRibbon={process.env.offerLabelHasRibbon}
          isTileDetailsAnimated={process.env.isTileDetailsAnimated}
          ctaIcon={process.env.ctaIcon}
          listIcon={process.env.listIcon}
          currencyFormatter={formatter}
          defaultProductImage={defaultProductImage}
          forYouLabel={t('common.user.profile.menu.for_you')}
        />
      ) : (
        groupedProducts.map((i, index) => {
          return (
            <div key={i.category} className={styles.catalogueRow}>
              <ProductCatalogueV2
                title={i.category}
                products={i.products}
                onListClick={openListSideSheet}
                checkStatus={checkListStatus}
                addProduct={product => {
                  addProduct(
                    product,
                    false,
                    {
                      item_list_id: 'offers',
                      item_list_name: selectedCategory ? selectedCategory : 'offers',
                    },
                    false,
                    t('common.asides.side_cart.no_comment')
                  );
                }}
                removeProduct={product =>
                  removeProduct(
                    product,
                    {
                      item_list_id: 'offers',
                      item_list_name: selectedCategory ? selectedCategory : 'offers',
                    },
                    t('common.asides.side_cart.no_comment')
                  )
                }
                image={index === 0 && side_banner ? side_banner : undefined}
                noItemsFoundWording={offersCatalogueWordings.noItemsFoundWording}
                noItemsFoundDescription={offersCatalogueWordings.noItemsFoundDescription}
                addItemWording={offersCatalogueWordings.addItemWording}
                hashTags={offersCatalogueWordings.hashTags}
                defaultQsText={t('common.qs.default')}
                defaultQsUnavailableText={t('common.qs.default_unavailable')}
                lastItemRef={index === groupedProducts.length - 1 ? lastResultItemRef : undefined}
                LinkComponent={props => {
                  const propsWithCleanUrl = getProductPropsWithCleanUrl(props);

                  return (
                    <Link
                      prefetch={false}
                      {...propsWithCleanUrl}
                      onClick={() => {
                        const product = getProductBasedOnSlug(i.products, propsWithCleanUrl.href);

                        if (product) {
                          selectItem(getProductInfo(product), {
                            item_list_id: 'offers',
                            item_list_name: selectedCategory ? selectedCategory : 'offers',
                            index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                          });
                        }
                      }}
                    />
                  );
                }}
                ImageComponent={CustomImage}
                offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                isTileDetailsAnimated={process.env.isTileDetailsAnimated}
                ctaIcon={process.env.ctaIcon}
                listIcon={process.env.listIcon}
                currencyFormatter={formatter}
                defaultProductImage={defaultProductImage}
                forYouLabel={t('common.user.profile.menu.for_you')}
              />
            </div>
          );
        })
      )}
      {isFetchingNextPage ? <ProductCatalogueV2 products={new Array(1)} isLoading loaderTilesAmount={5} /> : null}

      <SnackBar
        open={showSnackBar}
        setOpen={setShowSnackbar}
        text={offersCatalogueWordings.maxNumber}
        position={'center'}
      ></SnackBar>
      <OffersSnackbar
        emoji="🎉"
        open={showBundleOffersSnackBar}
        setOpen={BundleOfferSnackBarOpen.update}
        hasCloseOption={true}
        position="center"
        type="warning"
        autoWidth={true}
        buttonText={t('common.offers_snack_bar.btn_label')}
        text={getBundleOfferDescription(SelectedBundleOfferStore.getValue(), bundleOffersSummary)}
        onButtonPress={() => {
          setShowBundleOffersSnackBar(false);
          BundleOfferSnackBarOpen.update(false);
          BundleOfferSideSheetStore.update(true);
        }}
        autoHideDuration={8000}
        closeOnClickAway={true}
        // type = 'warning',
        // bold = BoldEnum.Bold,
        // className,
        hasShadow={true}
        // autoWidth = false,
      ></OffersSnackbar>
    </>
  );
}
